import type { Plugin } from "../../../core/plugin";
import { PluginRegistry } from "../../../core/pluginRegistry";

/**
 * ltnEcosystem — resaltado al pasar el ratón para el diagrama "Ecosistema
 * tecnológico" (LTNW-1).
 *
 * El prototipo construía todo el SVG y los ocho nodos en JavaScript, así que
 * el diagrama era invisible sin él. Aquí la geometría se calcula en build
 * time y se renderiza en servidor; este plugin solo enlaza cada nodo con su
 * radio y mueve la clase `is-active`.
 *
 * Contrato de marcado:
 *   <div data-plugin="ltnEcosystem">
 *     <line data-ltn-eco-spoke="0">        ← dentro del SVG inline
 *     <div data-ltn-eco-node="0" tabindex="0">
 */
export class LtnEcosystem implements Plugin {
  private nodes: HTMLElement[] = [];
  private spokes: SVGLineElement[] = [];
  private active = -1;

  private readonly onEnter = (event: Event) => {
    const node = (event.target as HTMLElement).closest<HTMLElement>(
      "[data-ltn-eco-node]",
    );
    this.setActive(node ? Number(node.dataset.ltnEcoNode) : -1);
  };

  private readonly onLeave = () => this.setActive(-1);

  constructor(private readonly element: HTMLElement) {}

  init(): void {
    this.nodes = [
      ...this.element.querySelectorAll<HTMLElement>("[data-ltn-eco-node]"),
    ];
    this.spokes = [
      ...this.element.querySelectorAll<SVGLineElement>("[data-ltn-eco-spoke]"),
    ];
    if (!this.nodes.length) return;

    this.element.addEventListener("mouseover", this.onEnter);
    this.element.addEventListener("focusin", this.onEnter);
    this.element.addEventListener("mouseleave", this.onLeave);
    this.element.addEventListener("focusout", this.onLeave);
  }

  destroy(): void {
    this.element.removeEventListener("mouseover", this.onEnter);
    this.element.removeEventListener("focusin", this.onEnter);
    this.element.removeEventListener("mouseleave", this.onLeave);
    this.element.removeEventListener("focusout", this.onLeave);
  }

  private setActive(index: number): void {
    if (index === this.active) return;
    this.active = index;

    this.nodes.forEach((node, i) =>
      node.classList.toggle("is-active", i === index),
    );
    this.spokes.forEach((spoke, i) =>
      spoke.classList.toggle("is-active", i === index),
    );
  }
}

PluginRegistry.register("ltnEcosystem", LtnEcosystem);
